import { Injectable } from '@angular/core';
import { IdProviderService } from 'libs/platform/dynamic-value-resolving/src/lib/services/id-provider.service';

/**
 * An IdProviderService is not injected at root or module level as IDs need to be unique within a certain scope.
 * Such scope can be a recommendation panel, or something else.
 * Everything within the same scope can resolve values from each other.
 * Components living in a different scope cannot resolve values from each other.
 * This is needed or else registering components by their IDs would class, as IDs may not be unique across scopes.
 */
@Injectable({
  providedIn: 'root',
})
export class IdProviderServiceFactoryService {
  private readonly map = new WeakMap<object, IdProviderService>();

  constructor() {}

  /**
   * Within the same scope the same IdProviderService is needed.
   * If a root requests an IdProviderService again, it is seen as the start of a new scope.
   * Therefore if there was an IdProviderService for the given scope already, it is first destroyed.
   * @param scope The scope for within the IdProviderService is used
   * @param isRoot Whether the requestor of the IdProviderService is the root element of the scope or not.
   * @returns A new or already existing IdProviderService
   */
  public get(scope: object, isRoot: boolean): IdProviderService {
    let idProviderService = this.map.get(scope);

    if (isRoot && idProviderService !== undefined) {
      idProviderService.ngOnDestroy();
      this.map.delete(scope);
      idProviderService = undefined;
    }

    if (idProviderService === undefined) {
      idProviderService = new IdProviderService();
      this.map.set(scope, idProviderService);
    }

    return idProviderService;
  }

  public destroy(scope: object): void {
    const idProviderService = this.map.get(scope);
    if (idProviderService !== undefined) {
      idProviderService.ngOnDestroy();
      this.map.delete(scope);
    }
  }
}
